import { createContext, useState } from 'react';
import { View, Text, Modal, Share } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import * as Clipboard from 'expo-clipboard';
import { UserPlus, Copy, Check, Share2 } from 'lucide-react-native';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Label } from './ui/Label';
import { useToast } from '../hooks/useToast';
import { clientLogger } from '../lib/datadog';
import { colors, spacing, typography } from '../lib/theme';
import { isIOS, isWeb } from '@/lib/platform';

const modalOverlayColor = 'rgba(0, 0, 0, 0.5)';

interface InviteModalContextType {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const InviteModalContext = createContext<InviteModalContextType | undefined>(undefined);

interface InviteModalProps {
  leagueId: string;
  leagueName: string;
  inviteCode: string;
}

export default function InviteModal({ leagueId: _leagueId, leagueName, inviteCode }: InviteModalProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);
  const { toast } = useToast();

  // Get base URL - for React Native, we'll use a placeholder or environment variable
  const baseUrl = isWeb && typeof window !== 'undefined' && typeof window.location !== 'undefined'
    ? window.location.origin
    : 'https://pckm.now';

  const inviteLink = `${baseUrl}/join/${inviteCode}`;

  const copyToClipboard = async (text: string, type: 'code' | 'link') => {
    try {
      await Clipboard.setStringAsync(text);

      if (type === 'code') {
        setCopiedCode(true);
        setTimeout(() => setCopiedCode(false), 2000);
      } else {
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
      }

      toast.show({
        type: 'success',
        title: 'Copied!',
        description: type === 'code' ? 'Invite code copied to clipboard' : 'Invite link copied to clipboard',
      });
    } catch (err) {
      clientLogger.error('Failed to copy invite details to clipboard', { error: err });
      toast.show({
        type: 'error',
        title: 'Failed to copy',
        description: 'Please try again',
      });
    }
  };

  const shareNatively = async () => {
    try {
      const result = await Share.share({
        message: `You've been invited to join the ${leagueName} NFL Pick'em league! Use code ${inviteCode} or visit ${inviteLink}`,
        title: `Join ${leagueName}`,
        url: inviteLink, // iOS specific
      });

      if (result.action === Share.sharedAction) {
        toast.show({
          type: 'success',
          title: 'Shared!',
          description: 'Invite shared successfully',
        });
      }
    } catch (err) {
      // User cancelled or error occurred
      clientLogger.error('Share error', { error: err });
    }
  };

  return (
    <InviteModalContext.Provider value={{ isOpen, setIsOpen }}>
      <View>
        <Button
          variant="default"
          size="sm"
          onPress={() => setIsOpen(true)}
          style={styles.triggerButton}
        >
          <UserPlus size={16} color={colors.text.primary} />
          <Text style={styles.triggerButtonText}>Invite Friends</Text>
        </Button>

        <Modal
          visible={isOpen}
          transparent
          animationType="slide"
          onRequestClose={() => setIsOpen(false)}
        >
          <View style={styles.overlay}>
            <View style={styles.content}>
              {/* Header */}
              <View style={styles.header}>
                <Text style={styles.title}>Invite Friends to {leagueName}</Text>
                <Text style={styles.description}>
                  Share this link or code with friends to invite them to your league.
                </Text>
              </View>

              {/* Invite Link */}
              <View style={styles.section}>
                <Label style={styles.label}>Invite Link</Label>
                <View style={styles.inputRow}>
                  <View style={styles.inputWrapper}>
                    <Input
                      value={inviteLink}
                      editable={false}
                      style={styles.input}
                    />
                  </View>
                  <Button
                    size="sm"
                    onPress={() => copyToClipboard(inviteLink, 'link')}
                    style={styles.copyButton}
                  >
                    {copiedLink ? (
                      <Check size={16} color={colors.text.primary} />
                    ) : (
                      <Copy size={16} color={colors.text.primary} />
                    )}
                  </Button>
                </View>
              </View>

              {/* Invite Code */}
              <View style={styles.section}>
                <Label style={styles.label}>Invite Code</Label>
                <View style={styles.inputRow}>
                  <View style={styles.inputWrapper}>
                    <Input
                      value={inviteCode}
                      editable={false}
                      style={[styles.input, styles.codeInput]}
                    />
                  </View>
                  <Button
                    size="sm"
                    onPress={() => copyToClipboard(inviteCode, 'code')}
                    style={styles.copyButton}
                  >
                    {copiedCode ? (
                      <Check size={16} color={colors.text.primary} />
                    ) : (
                      <Copy size={16} color={colors.text.primary} />
                    )}
                  </Button>
                </View>
                <Text style={styles.hint}>
                  Friends can use this code to join at any time
                </Text>
              </View>

              {/* Share Button */}
              <View style={styles.shareSection}>
                <Button
                  onPress={shareNatively}
                  variant="outline"
                  style={styles.shareButton}
                >
                  <View style={styles.shareButtonContent}>
                    <Share2 size={16} color={colors.text.primary} />
                    <Text style={styles.shareButtonText}>Share Invite</Text>
                  </View>
                </Button>
              </View>

              {/* Close Button */}
              <View style={styles.footer}>
                <Button
                  variant="outline"
                  onPress={() => setIsOpen(false)}
                  style={styles.closeButton}
                >
                  <Text style={styles.closeButtonText}>Close</Text>
                </Button>
              </View>
            </View>
          </View>
        </Modal>
      </View>
    </InviteModalContext.Provider>
  );
}

const styles = StyleSheet.create(() => ({
  closeButton: {
    width: '100%',
  },
  closeButtonText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold,
    textTransform: 'uppercase',
  },
  codeInput: {
    fontFamily: isIOS ? 'Courier' : 'monospace',
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.semibold,
    textAlign: 'center',
  },
  content: {
    backgroundColor: colors.card,
    borderColor: colors.cardBorder,
    borderRadius: 12,
    borderWidth: 1,
    elevation: 0,
    maxWidth: 500,
    padding: spacing.xl,
    shadowColor: 'transparent',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
    width: '100%',
  },
  copyButton: {
    alignItems: 'center',
    backgroundColor: colors.ink.acid,
    borderColor: colors.ink.acid,
    borderRadius: 8,
    borderWidth: 1,
    justifyContent: 'center',
    minWidth: 44,
    paddingHorizontal: spacing.md,
  },
  description: {
    color: colors.text.secondary,
    fontSize: typography.fontSize.base,
    lineHeight: 20,
  },
  footer: {
    marginTop: spacing.lg,
  },
  header: {
    marginBottom: spacing.xl,
  },
  hint: {
    color: colors.text.tertiary,
    fontSize: typography.fontSize.xs,
    marginTop: spacing.sm,
  },
  input: {
    color: colors.text.primary,
    fontSize: typography.fontSize.base,
  },
  inputRow: {
    flexDirection: 'row',
    gap: spacing.sm,
  },
  inputWrapper: {
    flex: 1,
  },
  label: {
    color: colors.text.secondary,
    fontFamily: typography.fontFamily.mono,
    fontSize: typography.fontSize.xs,
    fontWeight: typography.fontWeight.bold,
    letterSpacing: 0.8,
    marginBottom: spacing.sm,
    textTransform: 'uppercase',
  },
  overlay: {
    alignItems: 'center',
    backgroundColor: modalOverlayColor,
    flex: 1,
    justifyContent: 'center',
    padding: 20,
  },
  section: {
    marginBottom: spacing.lg,
  },
  shareButton: {
    width: '100%',
  },
  shareButtonContent: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: spacing.sm,
    justifyContent: 'center',
  },
  shareButtonText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: typography.fontSize.md,
    fontWeight: typography.fontWeight.bold,
    textTransform: 'uppercase',
  },
  shareSection: {
    borderTopColor: colors.cardBorder,
    borderTopWidth: 1,
    marginTop: spacing.xs,
    paddingTop: spacing.lg,
  },
  title: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.display,
    fontSize: typography.fontSize['2xl'],
    fontWeight: '900',
    marginBottom: spacing.sm,
    textTransform: 'uppercase',
  },
  triggerButton: {
    backgroundColor: colors.ink.acid,
    borderColor: colors.ink.acid,
    borderRadius: 8,
    borderWidth: 1,
    elevation: 0,
    shadowColor: 'transparent',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0,
    shadowRadius: 0,
  },
  triggerButtonText: {
    color: colors.text.primary,
    fontFamily: typography.fontFamily.mono,
    fontSize: typography.fontSize.sm,
    fontWeight: typography.fontWeight.bold,
    textTransform: 'uppercase',
  },
}));
